import React, { useState } from "react";
import { X, Search } from "lucide-react";
import { CORE_STYLES, BEER_STYLE_GROUPS, getFermentationFamily, getOriginClassification } from "../../utils/beerTaxonomy";
import BeerImage from "./BeerImage";

const getBeerCategoryColor = (category) => {
  const cat = (category || "").toLowerCase();
  if (cat.includes("crisp") || cat.includes("clean")) return "bg-[#EFF6FF] text-[#2563EB] border-[#BFDBFE]"; // Light Blue
  if (cat.includes("balanced") || cat.includes("toasty")) return "bg-[#FEFCE8] text-[#CA8A04] border-[#FEF08A]"; // Yellow
  if (cat.includes("bright") || cat.includes("hoppy")) return "bg-[#F0FDF4] text-[#16A34A] border-[#BBF7D0]"; // Green
  if (cat.includes("fresh") || cat.includes("lively") || cat.includes("fruity")) return "bg-[#FAF5FF] text-[#9333EA] border-[#E9D5FF]"; // Purple
  if (cat.includes("rich") || cat.includes("dark")) return "bg-[#111827] text-white border-[#1F2937]"; // Dark/Black
  return "bg-gray-50 text-gray-700 border-gray-200";
};

const getBeerLevelColor = (level) => {
  const lvl = (level || "").toLowerCase();
  if (lvl.includes("non-alcoholic") || lvl === "low" || lvl === "light") return "text-[#2563EB] bg-[#EFF6FF] border-[#BFDBFE]";
  if (lvl === "medium") return "text-[#CA8A04] bg-[#FEFCE8] border-[#FEF08A]";
  if (lvl === "high" || lvl === "strong") return "text-[#DC2626] bg-[#FEF2F2] border-[#FECACA]";
  return "text-gray-700 bg-gray-50 border-gray-200";
};

const getAbvCategory = (abv) => {
  if (abv === undefined || abv === null || abv === "") return null;
  const val = Number(abv);
  if (isNaN(val) || val < 0) return null;
  if (val < 0.5) return "Non-Alcoholic";
  if (val <= 4.5) return "Light";
  if (val <= 7.5) return "Medium";
  return "Strong";
};

const getIbuCategory = (ibu) => {
  if (ibu === undefined || ibu === null || ibu === "") return null;
  const val = Number(ibu);
  if (isNaN(val)) return null;
  if (val <= 20) return "Low";
  if (val <= 45) return "Medium";
  return "High";
};

// Renders one row per saved serving-size/price pair for a beer format,
// instead of collapsing multiple sizes (e.g. two Draft entries) into one.
const renderBeerFormatRows = (format, label, badgeClass) => {
  if (!format) return null;
  const sizes = format.serving_sizes?.length ? format.serving_sizes : ['oz'];
  return sizes.map((size, idx) => (
    <div className="flex items-center gap-3" key={`${label}-${idx}`}>
      <span className={`text-[11px] font-bold ${badgeClass} rounded px-2 py-1 uppercase min-w-[54px] text-center`}>{label}</span>
      <span className="text-lg font-black text-gray-900 ml-1">${format.prices?.[idx] ?? '0'}</span>
      <span className="text-[12px] text-gray-500 font-medium">/ {size || 'oz'}</span>
    </div>
  ));
};

const ReferenceMenuPanel = ({ isOpen, onClose, menuData }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("None");
  const [coreStyleFilter, setCoreStyleFilter] = useState("All");
  const [styleGroupFilter, setStyleGroupFilter] = useState("All");

  // Transform beer data
  const beerData = Array.isArray(menuData)
    ? menuData.map((beer) => {
        const abvVal = beer?.abv ?? 0;
        const ibuVal = beer?.ibu ?? 0;
        return {
          name: beer?.product_name || beer?.name || "Unknown Beer",
          producer: beer?.brewery_name || beer?.producer_name || "Unknown Brewery",
          category: beer?.category || "",
          style: beer?.style?.name || beer?.style || beer?.sub_category || "",
          body: beer?.body || beer?.style?.body || "",
          texture: beer?.texture || beer?.style?.texture || "",
          intensity: beer?.intensity || beer?.flavorIntensity || beer?.style?.flavor_intensity || "",
          abv: abvVal,
          ibu: ibuVal,
          abv_category: beer?.abv_category || getAbvCategory(abvVal),
          ibu_category: beer?.ibu_category || getIbuCategory(ibuVal),
          image_url: beer?.image_url || "",
          notes: beer?.notes || beer?.description || "",
          formats: beer?.formats || [],
          restaurantname: beer?.restaurantname || beer?.restaurantNameDisplay || beer?.restaurant_name || "",
          origin: {
            country: beer?.origin?.country || beer?.region?.country || "",
            state: beer?.origin?.state || beer?.region?.state || beer?.region?.region || "",
            city: beer?.origin?.city || beer?.region?.city || beer?.region?.sub_region || "",
          },
          region: [
            beer?.origin?.country || beer?.region?.country,
            beer?.origin?.state || beer?.region?.state || beer?.region?.region,
            beer?.origin?.city || beer?.region?.city || beer?.region?.sub_region
          ].filter(Boolean).join(" / ") || beer?.region || "-",
          is_vegan: Boolean(beer?.is_vegan || beer?.vegan === "Yes"),
          has_lactose: Boolean(beer?.has_lactose || beer?.lactose === "Yes"),
          is_non_alcoholic: Boolean(beer?.is_non_alcoholic || beer?.non_alcoholic === "Yes"),
          has_nuts: Boolean(beer?.has_nuts || beer?.nuts === "Yes"),
          is_filtered: Boolean(beer?.is_filtered || beer?.filtered === "Yes"),
          has_gluten: beer?.has_gluten === undefined ? true : Boolean(beer.has_gluten),
          seasonal: Boolean(beer?.seasonal),
          limited: Boolean(beer?.limited),
        };
      })
    : [];

  const handleOptionChange = (e) => {
    setFilterType(e.target.value);
  };

  const filteredBeerData = beerData.filter((beer) => {
    const matchesSearch =
      beer?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      beer?.producer?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      beer?.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      beer?.style?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      beer?.region?.toLowerCase().includes(searchTerm.toLowerCase());

    let matchesFilter = true;
    if (filterType === "draft") {
      matchesFilter = beer?.formats?.some((f) => f.format === "draft" && f.prices?.length > 0);
    } else if (filterType === "bottle") {
      matchesFilter = beer?.formats?.some((f) => f.format === "bottle" && f.prices?.length > 0);
    } else if (filterType === "can") {
      matchesFilter = beer?.formats?.some((f) => f.format === "can" && f.prices?.length > 0);
    }

    const matchesCoreStyle = coreStyleFilter === "All" || beer?.style === coreStyleFilter;
    const matchesStyleGroup = styleGroupFilter === "All" || beer?.category === styleGroupFilter;

    return matchesSearch && matchesFilter && matchesCoreStyle && matchesStyleGroup;
  });

  const beerCategories = Array.from(
    new Set(filteredBeerData.map((beer) => beer.category?.toLowerCase() || "general"))
  );

  const beersByCategory = beerCategories.reduce((acc, category) => {
    acc[category] = filteredBeerData.filter(
      (beer) => (beer.category?.toLowerCase() || "general") === category
    );
    return acc;
  }, {});

  return (
    <div
      className={`fixed inset-y-0 right-0 w-full max-w-[400px] bg-[#FFF9F3] shadow-lg z-[102] transform transition-transform duration-300 ease-in-out ${
        isOpen ? "translate-x-0" : "translate-x-full"
      }`}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
        <h2 className="text-lg font-bold text-black">Reference Menu</h2>
        <button
          onClick={onClose}
          className="text-gray-500 hover:text-red-500 p-1 rounded-full"
        >
          <X size={22} />
        </button>
      </div>

      <div className="p-4 overflow-y-auto h-[calc(100%-64px)]">
        {/* Search Bar */}
        <div className="mb-4">
          <div className="relative">
            <Search
              size={18}
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
            />
            <input
              type="text"
              placeholder="Search menu items..."
              className="w-full pl-10 pr-4 py-2 rounded-md bg-background border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#e11d48] text-black text-base"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Filter Bar */}
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-2 text-left">
            Filter Options
          </label>
          <div className="space-y-3">
            <div>
              <label
                htmlFor="by_the_filter"
                className="block text-xs font-medium text-gray-500 mb-1 text-left"
              >
                Serving Format
              </label>
              <div className="relative">
                <select
                  id="by_the_filter"
                  name="by_the_filter"
                  className="w-full pl-3 pr-9 py-2 rounded-md bg-background border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#e11d48] text-black text-sm appearance-none cursor-pointer truncate"
                  value={filterType}
                  onChange={handleOptionChange}
                >
                  <option value="None">All formats</option>
                  <option value="draft">Draft</option>
                  <option value="bottle">Bottle</option>
                  <option value="can">Can</option>
                </select>
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="core_style_filter" className="block text-xs font-medium text-gray-500 mb-1 text-left">
                Core Style
              </label>
              <div className="relative">
                <select
                  id="core_style_filter"
                  className="w-full pl-3 pr-9 py-2 rounded-md bg-background border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#e11d48] text-black text-sm appearance-none cursor-pointer truncate"
                  value={coreStyleFilter}
                  onChange={(e) => setCoreStyleFilter(e.target.value)}
                >
                  <option value="All">All core styles</option>
                  {CORE_STYLES.map((style) => (
                    <option key={style} value={style}>
                      {style}
                    </option>
                  ))}
                </select>
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>

            <div>
              <label htmlFor="style_group_filter" className="block text-xs font-medium text-gray-500 mb-1 text-left">
                Style Group
              </label>
              <div className="relative">
                <select
                  id="style_group_filter"
                  className="w-full pl-3 pr-9 py-2 rounded-md bg-background border border-gray-300 focus:outline-none focus:ring-1 focus:ring-[#e11d48] text-black text-sm appearance-none cursor-pointer truncate"
                  value={styleGroupFilter}
                  onChange={(e) => setStyleGroupFilter(e.target.value)}
                >
                  <option value="All">All style groups</option>
                  {BEER_STYLE_GROUPS.map((group) => (
                    <option key={group.group_name} value={group.group_name}>
                      {group.group_name}
                    </option>
                  ))}
                </select>
                <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Categories & Cards */}
        {beerCategories.map(
          (category) =>
            beersByCategory[category].length > 0 && (
              <div className="mb-6" key={category}>
                <h3 className="text-md text-left font-semibold text-black mb-3">
                  {category.charAt(0).toUpperCase() + category.slice(1)} Beers
                </h3>
                <div className="space-y-4">
                  {beersByCategory[category].map((beer, idx) => (
                    <div
                      key={beer.name + idx}
                      className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-md transition-shadow group flex flex-col h-full"
                    >
                      {/* Image Header with Badges */}
                      <div className="relative h-48 w-full flex-shrink-0">
                        <BeerImage
                          src={beer.image_url}
                          alt={beer.name}
                          className="w-full h-full object-cover"
                          fallbackClassName="w-full h-full object-contain"
                        />

                        <div className="absolute top-3 right-3 flex gap-2">
                          {getFermentationFamily(beer.style) && (
                            <span className="text-[11px] font-bold text-gray-800 bg-white px-2.5 py-1 rounded-full shadow-sm border border-gray-200 uppercase">
                              {getFermentationFamily(beer.style)}
                            </span>
                          )}
                          {getOriginClassification(beer.origin.country) && (
                            <span
                              className={`text-[11px] font-bold px-2.5 py-1 rounded-full shadow-sm border uppercase ${getOriginClassification(beer.origin.country) === "DOMESTIC"
                                ? "bg-[#16A34A] text-white border-[#16A34A]"
                                : "bg-amber-500 text-white border-amber-500"
                                }`}
                            >
                              {getOriginClassification(beer.origin.country)}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Card Body */}
                      <div className="p-4 flex text-start flex-col flex-grow justify-between min-w-0">
                        <div className="flex flex-col flex-grow min-w-0">
                          {/* Title Section */}
                          <div className="mb-4">
                            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-wide break-words">
                              {beer.restaurantname || "Unknown Restaurant"}
                            </p>
                            <p className="text-sm font-medium text-gray-500 mt-0.5 break-words">
                              {beer.producer}
                            </p>
                            <h2 className="text-[20px] font-black text-gray-900 leading-tight break-words">
                              {beer.name}
                            </h2>
                          </div>

                          {/* Origin Hierarchy */}
                          <div className="text-[12px] pb-4 border-b border-gray-100 min-w-0">
                            <div className="text-gray-400 font-bold uppercase tracking-wide text-[10px] mb-1">Origin</div>
                            {beer.origin.country ? (
                              <>
                                <div className="text-gray-900 font-bold break-words">{beer.origin.country}</div>
                                {beer.origin.state && (
                                  <div className="text-gray-700 pl-3 flex items-center gap-1 break-words">
                                    <span className="text-gray-400">↳</span>{beer.origin.state}
                                  </div>
                                )}
                                {beer.origin.city && (
                                  <div className="text-gray-700 pl-6 flex items-center gap-1 break-words">
                                    <span className="text-gray-400">↳</span>{beer.origin.city}
                                  </div>
                                )}
                              </>
                            ) : (
                              <div className="text-gray-400">-</div>
                            )}
                          </div>

                          {/* Classification */}
                          <div className="text-[11px] py-4 border-b border-gray-100 min-w-0">
                            <div className="text-gray-400 font-bold uppercase tracking-wide text-[10px] mb-2">Classification</div>
                            <div className="grid grid-cols-[72px_1fr] gap-y-2 gap-x-2 min-w-0 items-center">
                              <div className="text-gray-500 font-medium">Style Group:</div>
                              <div className="flex justify-start min-w-0">
                                {beer.category ? <span className={`border rounded-full px-2 py-0.5 text-[11px] font-bold whitespace-nowrap ${getBeerCategoryColor(beer.category)}`}>{beer.category}</span> : "-"}
                              </div>

                              <div className="text-gray-500 font-medium">Core Style:</div>
                              <div className="flex justify-start min-w-0">
                                {beer.style ? <span className={`border rounded-full px-2 py-0.5 text-[11px] font-bold whitespace-nowrap ${getBeerCategoryColor(beer.category)}`}>{beer.style}</span> : "-"}
                              </div>

                              <div className="text-gray-500 font-medium">Body:</div>
                              <div className="flex justify-start min-w-0">
                                {beer.body && beer.body !== "N/A" ? <span className="bg-[#F1F5F9] text-[#1E293B] border border-gray-200 rounded-md px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap">{beer.body}</span> : "-"}
                              </div>

                              <div className="text-gray-500 font-medium">Texture:</div>
                              <div className="flex justify-start min-w-0">
                                {beer.texture && beer.texture !== "N/A" ? <span className="bg-blue-50 text-blue-700 border border-blue-200 rounded-md px-2 py-0.5 text-[11px] font-semibold whitespace-nowrap">{beer.texture}</span> : "-"}
                              </div>

                              <div className="text-gray-500 font-medium">Flavor<br />Intensity:</div>
                              <div className="flex justify-start min-w-0">
                                {beer.intensity && beer.intensity !== "N/A" ? <span className="bg-purple-50 text-purple-700 border border-purple-200 rounded-md px-2 py-0.5 text-[11px] font-semibold break-words">{beer.intensity}</span> : "-"}
                              </div>
                            </div>
                          </div>

                          {/* ABV/IBU */}
                          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 py-4 border-b border-gray-100">
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <span className="text-[11px] font-bold text-gray-400">ABV</span>
                              <span className={`text-[11px] font-black border px-1.5 py-0.5 rounded whitespace-nowrap ${getBeerLevelColor(beer.abv_category || getAbvCategory(beer.abv))}`}>{beer.abv_category || getAbvCategory(beer.abv) || "-"}</span>
                              <span className="text-[11px] font-black border border-gray-200 bg-white px-1.5 py-0.5 rounded text-gray-900 whitespace-nowrap">{beer.abv || "0"}%</span>
                            </div>
                            <div className="flex items-center gap-1.5 flex-shrink-0">
                              <span className="text-[11px] font-bold text-gray-400">IBU</span>
                              <span className={`text-[11px] font-black border px-1.5 py-0.5 rounded whitespace-nowrap ${getBeerLevelColor(beer.ibu_category || getIbuCategory(beer.ibu))}`}>{beer.ibu_category || getIbuCategory(beer.ibu) || "-"}</span>
                              <span className="text-[11px] font-black border border-gray-200 bg-white px-1.5 py-0.5 rounded text-gray-900 whitespace-nowrap">{beer.ibu || "0"}</span>
                            </div>
                          </div>

                          {/* Notes */}
                          {beer.notes && beer.notes !== "N/A" && (
                            <div className="bg-[#FEFCE8] border border-[#FEF08A] rounded-lg p-3 my-4">
                              <div className="flex items-center gap-2 mb-1">
                                <div className="w-3 h-3 bg-[#EAB308] rounded-sm"></div>
                                <span className="text-[11px] font-bold text-gray-500 tracking-wider">
                                  NOTES
                                </span>
                              </div>
                              <p className="text-[12px] text-gray-600 italic leading-relaxed break-words">
                                {beer.notes}
                              </p>
                            </div>
                          )}

                          {/* Formats / Pricing Matrix */}
                          {beer.formats && beer.formats.length > 0 && (
                            <div className="space-y-3 my-4">
                              {renderBeerFormatRows(beer.formats.find((f) => f.format === "draft"), "DRAFT", "bg-[#DBEAFE] text-[#1D4ED8]")}
                              {renderBeerFormatRows(beer.formats.find((f) => f.format === "can"), "CAN", "bg-[#E5E7EB] text-[#374151]")}
                              {renderBeerFormatRows(beer.formats.find((f) => f.format === "bottle"), "BOTTLE", "bg-[#F3F4F6] text-[#4B5563]")}
                            </div>
                          )}

                          {/* Attributes */}
                          <div className="mb-2">
                            <div className="text-gray-400 font-bold uppercase tracking-wide text-[10px] mb-2">Attributes</div>
                            <div className="flex flex-wrap gap-2 text-[11px] font-bold">
                              {beer.has_gluten === false && (
                                <div className="flex items-center gap-1.5 text-[#15803D] bg-[#ECFDF5] px-2.5 py-1 rounded-full">
                                  Gluten-Free
                                </div>
                              )}
                              {beer.is_vegan && (
                                <div className="flex items-center gap-1.5 text-[#15803D] bg-[#ECFDF5] px-2.5 py-1 rounded-full">
                                  <svg
                                    width="12"
                                    height="11"
                                    viewBox="0 0 12 11"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      d="M6.375 1.50176C4.53281 1.50176 2.97422 2.7088 2.44453 4.37286C3.23203 3.97442 4.12031 3.75176 5.0625 3.75176H7.125C7.33125 3.75176 7.5 3.92051 7.5 4.12676C7.5 4.33301 7.33125 4.50176 7.125 4.50176H6.75H5.0625C4.67344 4.50176 4.29609 4.5463 3.93281 4.62833C3.32578 4.76661 2.76094 5.0127 2.25938 5.34786C0.897656 6.25489 0 7.80411 0 9.56426V9.93926C0 10.251 0.250781 10.5018 0.5625 10.5018C0.874219 10.5018 1.125 10.251 1.125 9.93926V9.56426C1.125 8.42286 1.61016 7.3963 2.38594 6.67676C2.85 8.4463 4.46016 9.75176 6.375 9.75176H6.39844C9.49453 9.73536 12 6.6838 12 2.92208C12 1.92364 11.8242 0.974421 11.5055 0.118952C11.4445 -0.0427669 11.2078 -0.0357356 11.1258 0.116608C10.6852 0.941608 9.81328 1.50176 8.8125 1.50176H6.375Z"
                                      fill="#15803D"
                                    />
                                  </svg>
                                  Vegan
                                </div>
                              )}
                              {beer.has_lactose && (
                                <div className="flex items-center gap-1.5 text-orange-600 bg-orange-50 px-2.5 py-1 rounded-full">
                                  Contains Lactose
                                </div>
                              )}
                              {beer.is_non_alcoholic && (
                                <div className="flex items-center gap-1.5 text-orange-600 bg-orange-50 px-2.5 py-1 rounded-full">
                                  Non-Alcoholic
                                </div>
                              )}
                              {beer.has_nuts && (
                                <div className="flex items-center gap-1.5 text-red-600 bg-red-50 px-2.5 py-1 rounded-full">
                                  Contains Nuts
                                </div>
                              )}
                              {beer.is_filtered && (
                                <div className="flex items-center gap-1.5 text-gray-700 bg-gray-100 px-2.5 py-1 rounded-full">
                                  Filtered
                                </div>
                              )}
                              {beer.seasonal && (
                                <div className="flex items-center gap-1.5 text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full">
                                  Seasonal
                                </div>
                              )}
                              {beer.limited && (
                                <div className="flex items-center gap-1.5 text-purple-700 bg-purple-50 px-2.5 py-1 rounded-full">
                                  Limited Release
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )
        )}

        {filteredBeerData.length === 0 && (
          <div className="text-center text-gray-600 mt-8">
            {searchTerm
              ? `No results found for "${searchTerm}"`
              : coreStyleFilter !== "All" || styleGroupFilter !== "All" || filterType !== "None"
              ? "No beers match the selected filters"
              : "No beer items available"}
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferenceMenuPanel;

